// src/features/auth/DeviceAccountGate.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, AlertCircle, UserPlus } from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";

import { db, functions } from "../../firebase";
import { TENANT_ID } from "../../tenant";
import { getDeviceLabel, getOrCreateDeviceId } from "../../lib/deviceIdentity";
import DeviceChangeVerification from "./DeviceChangeVerification";

const appConfigRef = () => {
  return doc(db, "tenants", TENANT_ID, "config", "app");
};

// ⚠この端末が前回「通してよい(allow_existing)」と判定された印。
//   判定の callable(resolveAccountAfterSmsLogin)は Cloud Functions のコールドスタートに当たると
//   数秒かかり、その間ずっと「確認しています」で止まって見えていた(LINEのサービス一覧から開くと
//   遅いことがある、の主因・2026-10-30)。一度信頼済みになった端末はサーバー側でも
//   trusted_device として通り続けるので、印があれば先に画面を出し、確認は裏で行う。
//   裏の確認で通さない結果が返ったら、そこで確認画面へ切り替える。
//   データの守りは Firestore ルール側にあり、この印で読めるものは増えない。
const ALLOWED_KEY = "spaceos_device_allowed_v1";

const allowedMark = (uid) => `${TENANT_ID}:${uid}:${getOrCreateDeviceId()}`;

const wasAllowedBefore = (uid) => {
  if (!uid) return false;
  try {
    return window.localStorage.getItem(ALLOWED_KEY) === allowedMark(uid);
  } catch (_e) {
    return false;
  }
};

const rememberAllowed = (uid, allowed) => {
  try {
    if (allowed) window.localStorage.setItem(ALLOWED_KEY, allowedMark(uid));
    else window.localStorage.removeItem(ALLOWED_KEY);
  } catch (_e) {
    /* noop */
  }
};

const DeviceAccountGate = ({
  user,
  children,
  onStartFreshProfile,
}) => {
  const [status, setStatus] = useState(() =>
    wasAllowedBefore(user?.uid) ? "allowed" : "checking"
  );
  const [deviceId, setDeviceId] = useState("");
  const [appConfig, setAppConfig] = useState(null);
  const [resolveResult, setResolveResult] = useState(null);
  const [error, setError] = useState("");

  const loadAppConfig = useCallback(async () => {
    try {
      const snap = await getDoc(appConfigRef());

      if (snap.exists()) {
        setAppConfig({ id: snap.id, ...snap.data() });
      }
    } catch (err) {
      console.warn("[DeviceAccountGate] app config load failed", err);
    }
  }, []);

  // background=true: 画面は出したまま裏で確認する(前回この端末が通っていたとき)
  const resolveAccount = useCallback(async ({ background = false } = {}) => {
    if (!user?.uid) {
      setStatus("blocked");
      return;
    }

    if (!background) setStatus("checking");
    setError("");

    try {
      const currentDeviceId = getOrCreateDeviceId();
      setDeviceId(currentDeviceId);

      const callable = httpsCallable(functions, "resolveAccountAfterSmsLogin");

      // 設定(確認画面でしか使わない)と判定は並べて取りに行く
      const [, res] = await Promise.all([
        loadAppConfig(),
        callable({
          tenantId: TENANT_ID,
          deviceId: currentDeviceId,
          deviceLabel: getDeviceLabel(),
        }),
      ]);

      const data = res.data || {};
      setResolveResult(data);

      if (data.action === "allow_existing") {
        rememberAllowed(user.uid, true);
        setStatus("allowed");
        return;
      }

      rememberAllowed(user.uid, false);

      if (data.action === "start_fresh") {
        setStatus("start_fresh");
        return;
      }

      if (
        data.action === "needs_device_verification" ||
        data.action === "needs_support"
      ) {
        setStatus("needs_verification");
        return;
      }

      setError("アカウント状態を確認できませんでした。");
      setStatus("error");
    } catch (err) {
      console.error("[DeviceAccountGate] resolve failed", err);

      // 裏の確認が通信で失敗しただけなら、出している画面はそのままにする
      if (background && err?.code !== "functions/unauthenticated") return;

      if (err?.code === "functions/unauthenticated") {
        setError("ログイン状態を確認できませんでした。再度ログインしてください。");
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError("アカウント確認に失敗しました。通信状況をご確認ください。");
      }

      setStatus("error");
    }
  }, [user?.uid, loadAppConfig]);

  useEffect(() => {
    const optimistic = wasAllowedBefore(user?.uid);
    if (optimistic) setStatus("allowed");
    resolveAccount({ background: optimistic });
  }, [resolveAccount, user?.uid]);

  const handleStartFresh = useCallback(() => {
    if (onStartFreshProfile) {
      onStartFreshProfile({
        reason: resolveResult?.reason || "start_fresh",
        deviceId,
      });

      setStatus("allowed");
      return;
    }

    setStatus("start_fresh");
  }, [onStartFreshProfile, resolveResult, deviceId]);
  
  const loadingView = useMemo(() => {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <Loader2 size={28} className="animate-spin" />
          <p className="text-sm font-bold">確認しています</p>
        </div>
      </div>
    );
  }, []);

  if (!user) return null;

  if (status === "checking") {
    return loadingView;
  }

  if (status === "allowed") {
    return children;
  }

  if (status === "needs_verification") {
    return (
      <DeviceChangeVerification
        deviceId={deviceId}
        appConfig={appConfig}
        reason={resolveResult?.reason}
        initialMaskedEmail={resolveResult?.maskedEmail || ""}
        onVerified={() => resolveAccount()}
        onStartFresh={
          resolveResult?.canStartFresh ? handleStartFresh : undefined
        }
      />
    );
  }

  if (status === "start_fresh") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
          <div className="w-14 h-14 rounded-3xl bg-gray-50 flex items-center justify-center mb-5">
            <UserPlus className="text-gray-900" size={28} />
          </div>

          <h1 className="text-2xl font-black text-gray-900 tracking-tight">
            プロフィールを入力
          </h1>

          <p className="mt-3 text-sm leading-7 text-gray-600 font-medium">
            ご利用を開始するため、プロフィールの入力をお願いします。
          </p>

          <p className="mt-2 text-sm leading-7 text-gray-500">
            この電話番号で新しくご利用を開始できます。
          </p>

          <button
            onClick={handleStartFresh}
            className="mt-7 w-full rounded-3xl bg-gray-900 text-white font-black py-4"
          >
            入力へ進む
          </button>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-5">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-sm border border-gray-100 p-6 text-center">
          <div className="w-14 h-14 rounded-3xl bg-red-50 flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="text-red-500" size={28} />
          </div>

          <h1 className="text-xl font-black text-gray-900">
            アカウント確認に失敗しました
          </h1>

          <p className="mt-3 text-sm leading-7 text-gray-600">
            {error || "時間をおいて再度お試しください。"}
          </p>

          <button
            onClick={() => resolveAccount()}
            className="mt-6 w-full rounded-3xl bg-gray-900 text-white font-black py-4"
          >
            もう一度確認する
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default DeviceAccountGate;